/**
 * One-time reset of the per-browser table cache.
 *
 * Two reasons, and both are about entries this code never agreed to.
 *
 * 1. The WRITE side has been generic since ad-hoc snapshots existed —
 *    `ViewsProvider.activateView` / `FiltersProvider.activateFilter` store the
 *    applied setup for whatever surface they are mounted with. The READ side was
 *    a per-page call that only two pages ever made. Every other table has been
 *    accumulating entries for months that nothing ever honoured; switching the
 *    read on would activate all of them at once. Two of them bite hard: a
 *    column set taken while the registrations list still seeded the COMPANION
 *    default carries `actionCompanion` and leaves that tab with no accept/decline
 *    column, and a months-old filter narrows a list AND its count badge for a
 *    reason its owner has long forgotten (the root event list's filter key is not
 *    even event-scoped — one entry dims the whole dashboard).
 * 2. The entry SHAPE changed. A cache entry now records which view the columns
 *    came from and which column list the SERVER held at the time (`baseViewId` /
 *    `baseFields`), which is what keeps it from overwriting a colleague's saved
 *    edit. Old entries carry neither, so they cannot be honoured anyway.
 *
 * So the lanes start clean, exactly once. The cost is stated rather than hidden:
 * a user who had an unnamed setup on the guest list — the one surface where the
 * old entries DID work — sees the plain default there one time. And because the
 * old entry doubled as the "I chose the plain list" record, a user whose role
 * points at a saved view gets that view back on the next load, and keeps it
 * until they apply the plain list once more; the record lives in its own key
 * from now on (`plainListKey`), which this reset deliberately does not collect.
 * Converting instead of clearing would buy back only the DEFAULT case: a
 * converted entry has no `baseFields`, so the freshness rule would drop it
 * again for every named view.
 *
 * Deliberately a one-shot migration and NOT a read-path filter: an entry written
 * from now on is a deliberate choice and must survive every reload.
 */
import { getPref, removePref, setPref } from "@/lib/storage/userPrefs";

export const TABLE_CACHE_RESET_KEY = "table-cache-v2-reset";

/**
 * Every ad-hoc entry, on every surface: `view-|filter-` + the surface (plus the
 * event id where the surface is event-scoped) + `-adhoc`. The root event list's
 * `view-event-adhoc` is covered by the same shape.
 */
const CACHE_KEY = /^(view|filter)-.+-adhoc$/;

export function resetTableCacheOnce(): void {
  if (typeof window === "undefined") return;
  if (getPref(TABLE_CACHE_RESET_KEY) === "1") return;

  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && CACHE_KEY.test(key)) keys.push(key);
    }
    // Collect first, delete after: removing while iterating shifts the indices
    // and skips every second match.
    //
    // Through `removePref`, not the raw store: `view-` and `filter-` are on the
    // cookie-promotion allow-list, so a leftover legacy cookie would be
    // promoted back into localStorage on the next read — after the reset. (No
    // such cookie should exist: these keys were introduced two months after the
    // cookie era ended. Cheap insurance all the same.)
    for (const key of keys) removePref(key);

    setPref(TABLE_CACHE_RESET_KEY, "1");
  } catch {
    // localStorage unavailable (private mode). Without the marker this simply
    // retries on the next load, which is harmless.
  }
}
